import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'

interface UseViewportFitHeightOptions {
  recalculateOnScroll?: boolean
  bottomInsetPx?: number
}

/** Vertical padding reserved for the panel header/footer chrome. */
const PANEL_CHROME_PADDING_PX = 30

/**
 * Dynamically sets `maxHeight` on the element so it never extends below
 * the viewport bottom. Recalculates on scroll and resize via
 * requestAnimationFrame to avoid layout thrashing.
 */
export function useViewportFitHeight(ref: RefObject<HTMLElement | null>, options?: UseViewportFitHeightOptions) {
  const rafRef = useRef(0)
  const recalculateOnScroll = options?.recalculateOnScroll ?? true
  const bottomInsetPx = options?.bottomInsetPx ?? 0
  const hasHadScrollOrResizeRef = useRef(false)
  const insetPxRef = useRef(bottomInsetPx)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    hasHadScrollOrResizeRef.current = false
    insetPxRef.current = bottomInsetPx

    function getOffsetInStickyParent(): number {
      let offset = 0
      let current: HTMLElement | null = el
      while (current) {
        offset += current.offsetTop
        const parent = current.offsetParent as HTMLElement | null
        if (!parent || parent === document.body) break
        const position = getComputedStyle(parent).position
        if (position === 'sticky') break
        current = parent
      }
      return offset
    }

    function measure() {
      rafRef.current = 0
      if (!el) return

      const currentInset = hasHadScrollOrResizeRef.current ? 0 : insetPxRef.current

      let available: number
      if (recalculateOnScroll) {
        available = Math.max(0, window.innerHeight - el.getBoundingClientRect().top)
      } else {
        const offsetInSidebar = getOffsetInStickyParent()
        available = Math.max(0, window.innerHeight - offsetInSidebar)
      }
      const adjustedAvailable = Math.max(0, available - currentInset)
      el.style.maxHeight = `${adjustedAvailable}px`
      el.style.setProperty('--panel-height', `${Math.max(0, adjustedAvailable - PANEL_CHROME_PADDING_PX)}px`)
    }

    function scheduleUpdate() {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(measure)
    }

    function onScrollOrResize() {
      hasHadScrollOrResizeRef.current = true
      scheduleUpdate()
    }

    measure()
    scheduleUpdate()

    if (recalculateOnScroll) {
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('scroll', onScrollOrResize)
    }
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      if (recalculateOnScroll) {
        window.removeEventListener('scroll', onScrollOrResize)
      }
      window.removeEventListener('resize', onScrollOrResize)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.style.maxHeight = ''
      el.style.removeProperty('--panel-height')
    }
  }, [ref, recalculateOnScroll, bottomInsetPx])
}
